import { useCallback, useEffect, useMemo, useState } from "react";
import { useLang } from "@/lib/lang";
import { importCaseQarark, suggestCaseQarark } from "@/lib/api";
import {
  getQararkSuggestCache,
  setQararkSuggestCache,
  suggestCacheKey,
} from "@/lib/qararkSuggestCache";
import QararkSettings from "@/components/QararkSettings";
import QararkJordanWarning from "@/components/QararkJordanWarning";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Settings2 } from "lucide-react";
import { toast } from "sonner";

const DOC_ROLES = ["law", "regulation", "reference"];

const ROLE_KEYS = {
  law: "case_doc_role_law",
  regulation: "case_doc_role_regulation",
  reference: "case_doc_role_reference",
};

function mergeResults(existing, incoming) {
  const seen = new Set((existing || []).map((r) => r.doc_id));
  const merged = [...(existing || [])];
  for (const hit of incoming || []) {
    if (!hit?.doc_id || seen.has(hit.doc_id)) continue;
    seen.add(hit.doc_id);
    merged.push(hit);
  }
  return merged;
}

function hydrateFromCache(cached) {
  return {
    connected: cached.connected ?? null,
    results: cached.results || [],
    suggestedQueries: cached.suggestedQueries || [],
    selected: cached.selected || {},
    roles: cached.roles || {},
  };
}

export default function CaseQararkPicker({
  caseMeta,
  caseId,
  active = true,
  importMode = false,
  onSelectionChange,
  onImported,
}) {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [connected, setConnected] = useState(null);
  const [results, setResults] = useState([]);
  const [suggestedQueries, setSuggestedQueries] = useState([]);
  const [selected, setSelected] = useState({});
  const [roles, setRoles] = useState({});
  const [manualQuery, setManualQuery] = useState("");
  const [qararkOpen, setQararkOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const title = caseMeta?.title || "";
  const description = caseMeta?.description || "";
  const practiceArea = caseMeta?.practice_area || "civil";

  const cacheKey = useMemo(
    () => suggestCacheKey({ caseId, title, description, practiceArea }),
    [caseId, title, description, practiceArea],
  );

  const selectedItems = useMemo(
    () =>
      Object.keys(selected)
        .filter((docId) => selected[docId])
        .map((docId) => {
          const hit = results.find((r) => r.doc_id === docId);
          return {
            doc_id: docId,
            title: hit?.title || docId,
            role: roles[docId] || hit?.suggested_role || "regulation",
          };
        }),
    [selected, results, roles],
  );

  useEffect(() => {
    onSelectionChange?.(selectedItems);
  }, [selectedItems, onSelectionChange]);

  const persistCache = useCallback(
    (overrides = {}) => {
      setQararkSuggestCache(cacheKey, {
        connected,
        results,
        suggestedQueries,
        selected,
        roles,
        ...overrides,
      });
    },
    [cacheKey, connected, results, suggestedQueries, selected, roles],
  );

  const applySuggestResponse = useCallback((res, merge = false) => {
    setConnected(Boolean(res.qarark_connected));
    if (res.suggested_queries?.length) {
      setSuggestedQueries(res.suggested_queries);
    }
    const incoming = res.results || [];
    setResults((prev) => (merge ? mergeResults(prev, incoming) : incoming));
    if (!merge) {
      setSelected({});
      setRoles({});
    }
  }, []);

  const runSuggest = useCallback(
    async (queries = null, merge = false) => {
      if (!active) return;
      setLoading(true);
      try {
        const payload = {
          title: title.trim(),
          description: description.trim(),
          practice_area: practiceArea,
          case_id: caseId || undefined,
        };
        if (queries?.length) {
          payload.queries = queries;
        }
        const res = await suggestCaseQarark(payload);
        applySuggestResponse(res, merge);
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    },
    [active, title, description, practiceArea, caseId, applySuggestResponse],
  );

  // Hydrate from cache or fetch once per cache key (survives tab/step remounts).
  useEffect(() => {
    if (!active) return;
    if (!title.trim() && !caseId) return;

    const cached = getQararkSuggestCache(cacheKey);
    if (cached) {
      const hydrated = hydrateFromCache(cached);
      setConnected(hydrated.connected);
      setResults(hydrated.results);
      setSuggestedQueries(hydrated.suggestedQueries);
      setSelected(hydrated.selected);
      setRoles(hydrated.roles);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const res = await suggestCaseQarark({
          title: title.trim(),
          description: description.trim(),
          practice_area: practiceArea,
          case_id: caseId || undefined,
        });
        if (cancelled) return;
        applySuggestResponse(res, false);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [active, cacheKey, title, description, practiceArea, caseId, applySuggestResponse]);

  // Persist selection / role changes without re-fetching.
  useEffect(() => {
    if (loading) return;
    if (connected === null && results.length === 0) return;
    persistCache();
  }, [loading, connected, results, suggestedQueries, selected, roles, persistCache]);

  const toggleSelect = (docId) => {
    setSelected((prev) => ({ ...prev, [docId]: !prev[docId] }));
  };

  const setRole = (docId, role) => {
    setRoles((prev) => ({ ...prev, [docId]: role }));
  };

  const handleManualSearch = () => {
    const q = manualQuery.trim();
    if (!q) return;
    runSuggest([q], true);
    setManualQuery("");
  };

  const handleQueryChip = (query) => {
    runSuggest([query], true);
  };

  const handleImport = async () => {
    if (!caseId || !selectedItems.length) {
      toast.message(t("qarark_select_one"));
      return;
    }
    setImporting(true);
    try {
      const res = await importCaseQarark(caseId, selectedItems);
      const count = res.saved?.length || 0;
      if (count) {
        toast.success(t("case_qarark_import_success").replace("{count}", String(count)));
        setSelected({});
        setRoles({});
        persistCache({ selected: {}, roles: {} });
        onImported?.();
      }
      if (res.errors?.length && !count) {
        toast.error(res.errors[0].detail || t("no_results"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setImporting(false);
    }
  };

  if (!active) return null;

  const sectionBody = (
    <div className="space-y-3">
      {connected === false && (
        <div className="grid-border p-3 bg-amber-50/60 flex flex-wrap items-center gap-2">
          <p className="text-xs text-slate-700 flex-1 min-w-[180px]">{t("case_qarark_not_connected")}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-none h-7 text-[10px] gap-1"
            onClick={() => setQararkOpen(true)}
          >
            <Settings2 size={12} />
            {t("case_qarark_connect_btn")}
          </Button>
        </div>
      )}

      {connected && (
        <>
          {suggestedQueries.length > 0 && (
            <div>
              <span className="eyebrow block mb-1.5">{t("case_qarark_suggested_queries")}</span>
              <div className="flex flex-wrap gap-1.5">
                {suggestedQueries.map((q) => (
                  <button
                    key={q}
                    type="button"
                    className="text-[10px] px-2 py-1 grid-border bg-white hover:bg-slate-50 text-slate-700"
                    onClick={() => handleQueryChip(q)}
                    disabled={loading}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Input
              value={manualQuery}
              onChange={(e) => setManualQuery(e.target.value)}
              placeholder={t("case_qarark_search_placeholder")}
              className="rounded-none grid-border text-sm h-9 flex-1"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleManualSearch();
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-none h-9 text-xs gap-1 shrink-0"
              onClick={handleManualSearch}
              disabled={loading || !manualQuery.trim()}
            >
              <Search size={14} />
              {t("case_qarark_search_more")}
            </Button>
          </div>
        </>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-xs text-slate-500 py-4">
          <Loader2 size={14} className="animate-spin" />
          {t("case_qarark_loading")}
        </div>
      )}

      {!loading && connected && results.length === 0 && (
        <p className="text-xs text-slate-500 py-2">{t("case_qarark_empty")}</p>
      )}

      {!loading && results.length > 0 && (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {results.map((hit) => {
            const role = roles[hit.doc_id] || hit.suggested_role || "regulation";
            return (
              <label
                key={hit.doc_id}
                className="flex gap-2 items-start grid-border p-2 bg-white cursor-pointer hover:bg-slate-50/80"
              >
                <input
                  type="checkbox"
                  checked={Boolean(selected[hit.doc_id])}
                  onChange={() => toggleSelect(hit.doc_id)}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-900 line-clamp-2">{hit.title}</p>
                  {hit.snippet && hit.snippet !== hit.title && (
                    <p className="text-[10px] text-slate-500 line-clamp-2 mt-0.5">{hit.snippet}</p>
                  )}
                  {selected[hit.doc_id] && (
                    <div className="mt-1.5 flex items-center gap-2">
                      <span className="text-[10px] text-slate-500">{t("case_qarark_role_label")}</span>
                      <select
                        value={role}
                        onChange={(e) => setRole(hit.doc_id, e.target.value)}
                        className="text-[10px] grid-border px-1.5 py-0.5 bg-white"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {DOC_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {t(ROLE_KEYS[r])}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      )}

      {importMode && connected && selectedItems.length > 0 && (
        <Button
          type="button"
          className="rounded-none bg-slate-900 text-white w-full h-8 text-xs"
          onClick={handleImport}
          disabled={importing}
        >
          {importing ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            t("case_qarark_include")
          )}
        </Button>
      )}

      {!importMode && selectedItems.length > 0 && (
        <p className="text-[10px] text-slate-500">
          {t("case_qarark_included_count").replace("{count}", String(selectedItems.length))}
        </p>
      )}
    </div>
  );

  if (importMode) {
    return (
      <div className="grid-border-b" data-testid="case-qarark-picker">
        <QararkSettings open={qararkOpen} onOpenChange={setQararkOpen} />
        <button
          type="button"
          className="w-full px-4 py-2 bg-slate-50 flex items-center justify-between text-left"
          onClick={() => setExpanded((v) => !v)}
        >
          <span className="text-sm font-medium text-slate-900">{t("case_qarark_find_section")}</span>
          <span className="text-xs text-slate-500">{expanded ? "−" : "+"}</span>
        </button>
        {expanded && <div className="p-4">{sectionBody}</div>}
      </div>
    );
  }

  return (
    <div className="grid-border p-4 bg-slate-50/30" data-testid="case-qarark-picker">
      <QararkSettings
        open={qararkOpen}
        onOpenChange={(open) => {
          setQararkOpen(open);
          if (!open) runSuggest();
        }}
      />
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-slate-900">{t("case_qarark_section")}</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="rounded-none h-7 text-[10px] gap-1"
          onClick={() => setQararkOpen(true)}
        >
          <Settings2 size={12} />
          {t("qarark_settings")}
        </Button>
      </div>
      <QararkJordanWarning className="mb-3" />
      {sectionBody}
    </div>
  );
}
